/* ============================================================================
 * Movatic WMS — Coletor | ColetorScanScreen (layout base scan-driven) | CesarFilho 932048016
 * Arquivo: src/mobile/components/ColetorScanScreen.tsx
 * ----------------------------------------------------------------------------
 * Layout base unificado de Scan/Consulta (ADR-0034 F3). Uma so' tela, 4 zonas,
 * parametrizada por modo:
 *   - 'unico'    : uma leitura -> resultado fica fixo (Consulta).
 *   - 'continuo' : rearma a cada leitura (Scan em rajada), com badge "armado".
 * Paleta B, dark-only (tokens var(--*)); sem cores Tailwind cravadas.
 * O verde (var(--col-success)) e' EXCLUSIVO de estado (badge "armado");
 * as acoes (Buscar/Camara) sao azul var(--col-action).
 * ========================================================================== */

import { useEffect, useRef, useState } from 'react'
import { Camera, Search, Package } from 'lucide-react'
import CameraScanner from './CameraScanner'
import { useKeyboardScanner } from '../hooks/useKeyboardScanner'
import { useStockScan } from '../../lib/hooks/useStockScan'
import { sanitizeScan } from '@/lib/scan'

export type ColetorScanMode = 'unico' | 'continuo'

export interface ColetorScanScreenProps {
  mode: ColetorScanMode
  titulo: string
}

export default function ColetorScanScreen({
  mode,
  titulo,
}: ColetorScanScreenProps) {
  const [term, setTerm] = useState('')
  const [q, setQ] = useState('')
  const [lastCode, setLastCode] = useState('')
  // 'continuo' arranca a ler; 'unico' abre camara so' a pedido. Mais divergencia (perfil/autorizacao) vira depois.
  const [showCam, setShowCam] = useState(mode === 'continuo')
  const [imgError, setImgError] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  const { data, isFetching, error } = useStockScan(q, !!q)

  // Captura rajadas do Zebra/teclado. Opcoes REAIS do hook (sem allowInInputs/as any).
  useKeyboardScanner((scanned: string) => submit(scanned), {
    preventDefault: true,
  })

  // Foco inicial.
  useEffect(() => {
    inputRef.current?.focus()
  }, [])

  // A foto e' por leitura: limpa o erro de imagem a cada nova consulta.
  useEffect(() => {
    setImgError(false)
  }, [q])

  function submit(raw?: string) {
    const cleaned = sanitizeScan(raw ?? term)
    if (!cleaned || isFetching) return
    setQ(cleaned)
    setLastCode(cleaned)
    setTerm('') // limpa p/ proxima leitura
    // 'unico': o resultado fica fixo; 'continuo': rearma para a proxima.
    // Em ambos devolvemos o foco ao input (nao re-dispara sozinho).
    inputRef.current?.focus()
  }

  const armado = mode === 'continuo'
  const found = !!data?.found
  const firstImg = found ? data?.images?.[0]?.url : undefined
  const showImg = !!firstImg && !imgError

  return (
    <div className="space-y-3">
      {/* ====================== ZONA 1 — cabecalho + scan ====================== */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <div
            className="text-base font-semibold"
            style={{ color: 'var(--tx-primary)' }}
          >
            {titulo}
          </div>
          {armado && (
            <span
              className="text-[11px] font-semibold px-2 py-0.5 rounded-full"
              style={{
                color: 'var(--col-success)',
                background: 'var(--bg-success-tile)',
              }}
            >
              ● armado
            </span>
          )}
        </div>

        <div className="flex gap-2">
          <input
            ref={inputRef}
            value={term}
            onChange={(e) => setTerm(sanitizeScan(e.target.value))}
            onPaste={(e) => {
              e.preventDefault()
              setTerm(sanitizeScan(e.clipboardData?.getData('text') ?? ''))
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault()
                submit()
              }
            }}
            placeholder="Digite ou escaneie (SKU / EAN / Serial)"
            inputMode="decimal"
            autoComplete="off"
            autoCapitalize="off"
            spellCheck={false}
            className="flex-1 rounded-xl px-3 py-3 text-sm outline-none"
            style={{
              background: 'var(--bg-card)',
              border: '0.5px solid var(--bd-card)',
              color: 'var(--tx-primary)',
            }}
          />
          <button
            type="button"
            onClick={() => setShowCam((v) => !v)}
            className="rounded-xl px-3 text-xs font-semibold active:translate-y-[1px] flex items-center"
            style={{
              background: 'var(--col-action)',
              color: 'var(--tx-primary)',
            }}
            title="Camara"
          >
            <Camera size={16} />
          </button>
          <button
            type="button"
            onClick={() => submit()}
            disabled={isFetching || !sanitizeScan(term)}
            className="rounded-xl px-3 text-xs font-semibold active:translate-y-[1px] disabled:opacity-60 flex items-center"
            style={{
              background: 'var(--col-action)',
              color: 'var(--tx-primary)',
            }}
            title="Buscar"
          >
            {isFetching ? '...' : <Search size={16} />}
          </button>
        </div>

        <div className="text-xs" style={{ color: 'var(--tx-secondary)' }}>
          {lastCode ? (
            <>
              Ultimo codigo: <b>{lastCode}</b>
            </>
          ) : (
            'Aguardando leitura...'
          )}
          {error && (
            <span className="ml-2" style={{ color: 'var(--col-danger-tx)' }}>
              • {String(error?.message ?? 'Erro')}
            </span>
          )}
        </div>

        {showCam && (
          <div
            className="rounded-xl overflow-hidden"
            style={{ border: '0.5px solid var(--bd-card)' }}
          >
            <CameraScanner
              onRead={(c: string) => {
                setShowCam(false)
                submit(sanitizeScan(c))
              }}
            />
          </div>
        )}
      </div>

      {/* resultado so' quando ha consulta com produto encontrado */}
      {q && found && (
        <>
          {/* ==================== ZONA 2 — retrato (foto/placeholder) ==================== */}
          <div
            className="rounded-xl overflow-hidden flex items-center justify-center"
            style={{
              background: 'var(--bg-card-muted)',
              border: '0.5px solid var(--bd-card)',
              minHeight: 140,
            }}
          >
            {showImg ? (
              <img
                src={firstImg}
                alt={data?.product?.name ?? 'produto'}
                onError={() => setImgError(true)}
                className="w-full object-cover"
                style={{ maxHeight: 200 }}
              />
            ) : (
              <div
                className="flex flex-col items-center gap-1 py-8"
                style={{ color: 'var(--tx-secondary)' }}
              >
                <Package size={28} />
                <span className="text-[10px]">sem imagem</span>
              </div>
            )}
          </div>

          {/* ==================== ZONA 3 — dados-chave ==================== */}
          <div
            className="rounded-xl p-3"
            style={{
              background: 'var(--bg-card)',
              border: '0.5px solid var(--bd-card)',
            }}
          >
            <div
              className="font-semibold"
              style={{ color: 'var(--tx-primary)' }}
            >
              {data?.product?.name ?? '—'}
            </div>
            <div
              className="text-xs mt-0.5 font-mono"
              style={{ color: 'var(--tx-secondary)' }}
            >
              {data?.product?.code ?? data?.product?.ean ?? '—'}
            </div>
            <div
              className="mt-2 text-2xl font-bold"
              style={{ color: 'var(--tx-primary)' }}
            >
              {data?.total?.qty ?? 0}{' '}
              <span
                className="text-sm font-medium"
                style={{ color: 'var(--tx-secondary)' }}
              >
                {data?.total?.uom ?? 'UN'}
              </span>
            </div>
          </div>

          {/* ==================== ZONA 4 — localizacoes ==================== */}
          <div
            className="rounded-xl p-3"
            style={{
              background: 'var(--bg-card)',
              border: '0.5px solid var(--bd-card)',
            }}
          >
            <div
              className="text-xs font-semibold mb-2"
              style={{ color: 'var(--tx-secondary)' }}
            >
              Por localizacao
            </div>
            {data?.locations?.length ? (
              <div className="space-y-1">
                {data.locations.map((l, i) => (
                  <div
                    key={i}
                    className="flex items-center justify-between text-sm"
                  >
                    <span
                      className="font-mono"
                      style={{ color: 'var(--tx-primary)' }}
                    >
                      {l.location}
                    </span>
                    <span style={{ color: 'var(--tx-secondary)' }}>
                      {l.qty}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-xs" style={{ color: 'var(--tx-secondary)' }}>
                Sem stock localizado.
              </div>
            )}
          </div>
        </>
      )}

      {/* nao encontrado */}
      {q && data && !found && !isFetching && (
        <div
          className="rounded-xl p-3 text-sm"
          style={{
            background: 'var(--bg-card-muted)',
            border: '0.5px solid var(--bd-card)',
            color: 'var(--tx-secondary)',
          }}
        >
          Nenhum produto para <b style={{ color: 'var(--tx-primary)' }}>{q}</b>.
        </div>
      )}

      {/* estado inicial */}
      {!q && (
        <div className="text-sm" style={{ color: 'var(--tx-secondary)' }}>
          Informe um codigo e toque em buscar.
        </div>
      )}
    </div>
  )
}
